import EmojiFlagsIcon from '@mui/icons-material/EmojiFlags';
import InfoIcon from '@mui/icons-material/Info';
import LanguageIcon from '@mui/icons-material/Language';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { Link, Tab, Tabs, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { FormikProvider, useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  generatePath,
  useBeforeUnload,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { ValidationError } from 'yup';

import pitchbook from '~/assets/icons/pitchbook.svg';
import { UNKNOWN_OPTION_VALUE } from '~/components/Autocomplete';
import { CompanyHistoryChanges } from '~/components/CompanyHistoryChanges';
import { ConfirmModal } from '~/components/ConfirmModal';
import { FullScreenLoader } from '~/components/FullScreenLoader';
import { StatusSteps } from '~/components/StatusSteps';
import { ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import {
  useCreateCompanyMutation,
  useGetSecurityTypesQuery,
  useLazyGetDraftCompanyQuery,
  useSaveCompanyAsDraftMutation,
  useUnlockCompanyMutation,
} from '~/services/rtkQuery/company';
import { CreateCompanyVariables } from '~/services/rtkQuery/company/types';
import { useAppSelector } from '~/store/hooks';
import { selectGeneratedLink } from '~/store/slices/generatedLink/selectors';
import { CreateCompanyFormValues } from '~/types/formValues';
import { CompanyStatus } from '~/types/models';
import { calculateCompanyFilledPercent, calculateRoundSummary } from '~/utils/calculations';
import { formatUrl } from '~/utils/formats';
import { companyNameSchema, companyWebSchema, createCompanyFormSchema } from '~/utils/formSchemas';
import { handleCompanyFormikErrors } from '~/utils/helpers';
import {
  transformCompanyValidationErrors,
  transformDraftCompanyForCreate,
} from '~/utils/transforms';

import { ConcurrentMode } from './components/ConcurrentMode';
import { EditingBanner } from './components/EditingBanner';
import { GeneralInfo } from './components/GeneralInfo';
import { RoundRef } from './components/GeneralInfo/components/Round';
import { ProgressBarContainer } from './components/ProgressBarContainer';
import styles from './styles.module.scss';

export const subroundInitialValue: CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number] =
  {
    id: '',
    dealDate: null,
    announcedDate: null,
    dealSize: '',
    securityType: null,
    cap: '',
    discount: '',
    interest: '',
    conversionAmount: '',
    preMoneyValuation: '',
    postMoneyValuation: '',
    acquiredPercent: '',
    acquiredCalc: '',
    employees: '',
    ceo: {
      name: '',
      education: null,
      biography: '',
      educationHistory: '',
    },
    ceoIsFounder: false,
    ceoChanged: false,
    generatingRevenue: false,
    participatingPreferred: false,
    noAnnouncement: false,
    revenueStatusChanged: false,
    generatingProfit: false,
    investors: [],
  };

export const CreateCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const { handleApiError, showNotification } = useNotification();
  const location = useLocation();
  const draftCompanyPbId: string | null = (location.state && location.state.companyPbId) || null;
  const initialData: CreateCompanyFormValues | null =
    (location.state && location.state.initialData) || null;
  const params = useParams<{ id: string }>();
  const companyId = params.id ? +params.id : null;
  const generatedLink = useAppSelector(selectGeneratedLink);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [companyHasHistory, setCompanyHasHistory] = useState(false);
  const [concurrentModeLoading, setConcurrentModeLoading] = useState(false);
  const [companyStatus, setCompanyStatus] = useState<CompanyStatus | null>(null);
  const [externalUserEmail, setExternalUserEmail] = useState<string | null>(null);
  const [companyLocked, setCompanyLocked] = useState(false);
  const [existingCompany, setExistingCompany] = useState<{
    id: number;
    status: CompanyStatus;
  } | null>(null);
  const roundRefs = useRef<(RoundRef | null)[]>([]);
  const securityTypesQuery = useGetSecurityTypesQuery();
  const [lazyGetDraftCompanyQuery, { isFetching: isLoadingLazyGetDraftCompanyQuery }] =
    useLazyGetDraftCompanyQuery();
  const [saveCompanyAsDraftMutation, { isLoading: isLoadingSaveCompanyAsDraftMutation }] =
    useSaveCompanyAsDraftMutation();
  const [createCompanyMutation] = useCreateCompanyMutation();
  const [unlockCompanyMutation] = useUnlockCompanyMutation();
  const formikInitialValues = useMemo<CreateCompanyFormValues>(() => {
    if (initialData) {
      return initialData;
    }

    return {
      form: {
        name: '',
        formerName: '',
        web: '',
        pitchbookUrl: '',
        foundedYear: '',
        industries: [],
        firstFinancingDate: null,
        firstFinancingDealType: null,
        bio: '',
        competitors: [],
        comments: '',
        rounds: [
          {
            name: 'A',
            subrounds: [{ ...subroundInitialValue, id: uuidv4() }],
          },
          {
            name: 'B',
            subrounds: [{ ...subroundInitialValue, id: uuidv4() }],
          },
          {
            name: 'C',
            subrounds: [
              {
                ...subroundInitialValue,
                id: uuidv4(),
                securityType: securityTypesQuery.data
                  ? {
                      value: securityTypesQuery.data.result.data[0].id,
                      label: securityTypesQuery.data.result.data[0].name,
                    }
                  : null,
              },
            ],
          },
        ],
      },
    };
  }, [initialData, securityTypesQuery.data]);
  const formik = useFormik<CreateCompanyFormValues>({
    initialValues: formikInitialValues,
    validationSchema: createCompanyFormSchema,
    onSubmit: async formValues => {
      try {
        const values = formValues.form;
        let allRoundSummariesValid = true;

        if (values.firstFinancingDate && values.firstFinancingDealType) {
          const data: CreateCompanyVariables = {
            id: companyId || undefined,
            pbId: draftCompanyPbId,
            bio: values.bio,
            comments: values.comments,
            competitors:
              values.competitors[0].value === UNKNOWN_OPTION_VALUE
                ? null
                : values.competitors.map(competitor =>
                    typeof competitor.value === 'number'
                      ? {
                          name: competitor.label,
                          external_id: competitor.value,
                          // @ts-expect-error meta will have pbId if meta exists
                          pbId: competitor.meta?.pbId,
                        }
                      : { name: competitor.value },
                  ),
            firstFinancingDate: values.firstFinancingDate,
            firstFinancingDealType: +values.firstFinancingDealType.value,
            formerName: values.formerName,
            foundedYear: values.foundedYear,
            industries: values.industries.map(industry => +industry.value),
            name: values.name.trim(),
            pitchbookUrl: values.pitchbookUrl,
            web: values.web,
            progressBar: 1,
            // @ts-expect-error dealDate should be present
            rounds: values.rounds.map(round => {
              const summary = calculateRoundSummary(round, values.firstFinancingDate);

              if (!summary.isValid) {
                allRoundSummariesValid = false;
              }

              return {
                name: round.name,
                subrounds: round.subrounds.map((subround, index) => ({
                  index,
                  acquiredCalc: +subround.acquiredCalc,
                  acquiredPercent: +subround.acquiredPercent,
                  announcedDate: subround.announcedDate,
                  isAnnounced: !subround.noAnnouncement,
                  ceo: {
                    biography: subround.ceo.biography,
                    education: subround.ceo.education ? +subround.ceo.education.value : null,
                    name: subround.ceo.name,
                    educationHistory: subround.ceo.educationHistory,
                  },
                  ceoChanged: subround.ceoChanged,
                  ceoIsFounder: subround.ceoIsFounder,
                  dealDate: subround.dealDate,
                  dealSize: +subround.dealSize,
                  employees: +subround.employees,
                  generatingProfit: subround.generatingProfit,
                  generatingRevenue: subround.generatingRevenue,
                  investors: subround.investors.map(investor => ({
                    corporate: investor.corporate,
                    external_id: typeof investor.id === 'number' ? investor.id : null,
                    lead: investor.lead,
                    name: investor.name,
                    new: investor.new,
                    pb_id: investor.pbId || null,
                  })),
                  participatingPreferred: subround.participatingPreferred,
                  postMoneyValuation: +subround.postMoneyValuation,
                  preMoneyValuation: +subround.preMoneyValuation,
                  revenueStatusChanged: subround.revenueStatusChanged,
                  securityType: subround.securityType ? +subround.securityType.value : undefined,
                  cap: +subround.cap || undefined,
                  discount: +subround.discount || undefined,
                  interest: +subround.interest || undefined,
                  conversionAmount: +subround.conversionAmount || undefined,
                })),
                summary: summary.data,
              };
            }),
          };

          if (allRoundSummariesValid) {
            const response = await createCompanyMutation(data).unwrap();

            showNotification({
              type: 'success',
              title: response.title,
              message: response.message,
            });

            navigate(ROUTES.COMPANIES);
          }
        }
      } catch (e) {
        const errors = transformCompanyValidationErrors(e);

        if (errors) {
          setTimeout(async () => {
            await handleCompanyFormikErrors(formik, errors, showNotification, roundRefs);

            formik.setErrors(errors);
          }, 1000);
        } else {
          handleApiError(e);
        }
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        if (companyId) {
          const data = await lazyGetDraftCompanyQuery({ id: companyId }).unwrap();

          const initialData = transformDraftCompanyForCreate(data.result.data);

          formik.resetForm({ values: initialData });

          setCompanyHasHistory(!!data.result.data.uuid);
          setCompanyStatus(data.result.data.status);

          if (data.result.data.status === 'pending' && !generatedLink) {
            setCompanyLocked(true);
            setExternalUserEmail(data.result.data.external_user_email);
          }
        }
      } catch (e) {
        navigate(ROUTES.NOT_FOUND, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, handleApiError, lazyGetDraftCompanyQuery, generatedLink]);

  useEffect(() => {
    return () => {
      if (!companyLocked && !generatedLink && companyId && companyStatus === 'draft') {
        unlockCompanyMutation({ id: companyId });
      }
    };
  }, [companyLocked, companyId, unlockCompanyMutation, generatedLink, companyStatus]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !formik.isSubmitting &&
      !isLoadingSaveCompanyAsDraftMutation &&
      formik.dirty &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useBeforeUnload(
    useCallback(
      event => {
        if (formik.dirty) {
          event.preventDefault();
          event.returnValue = '';
          return '';
        }

        return undefined;
      },
      [formik.dirty],
    ),
  );

  const hideEditingBanner = useCallback(() => {
    setExternalUserEmail(null);
  }, []);

  const handleExistingCompanyProfileView = useCallback(() => {
    if (existingCompany) {
      formik.resetForm();

      setExistingCompany(null);

      setTimeout(() => {
        navigate(
          generatePath(existingCompany.status === 'draft' ? ROUTES.EDIT_COMPANY : ROUTES.COMPANY, {
            id: existingCompany.id.toString(),
          }),
          { replace: true },
        );
      }, 0);
    }
  }, [existingCompany, formik, navigate]);

  const handleActiveTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  }, []);

  const handleSaveAsDraft = useCallback(
    async (submitGeneratedLink?: boolean) => {
      try {
        const values = formik.values.form;
        const fieldsWithErrors: string[] = [];

        try {
          await companyNameSchema.validate(values.name);
        } catch (e) {
          const error = e as ValidationError;

          formik.setFieldTouched('form.name');
          formik.setFieldError('form.name', error.message);

          fieldsWithErrors.push('Company Name');
        }

        try {
          await companyWebSchema.validate(values.web);
        } catch (e) {
          const error = e as ValidationError;

          formik.setFieldTouched('form.web');
          formik.setFieldError('form.web', error.message);

          fieldsWithErrors.push('Web');
        }

        if (fieldsWithErrors.length) {
          showNotification({
            type: 'error',
            title: 'Save Draft',
            message: 'Submission failed due to errors in the following section(s): General Info',
          });

          return;
        }

        const response = await saveCompanyAsDraftMutation({
          external_user: generatedLink ? true : undefined,
          action: generatedLink ? (submitGeneratedLink ? 'submit' : 'save') : undefined,
          id: companyId || undefined,
          pbId: draftCompanyPbId,
          bio: values.bio || null,
          comments: values.comments || null,
          competitors:
            values.competitors[0]?.value === UNKNOWN_OPTION_VALUE
              ? null
              : values.competitors.map(competitor =>
                  typeof competitor.value === 'number'
                    ? {
                        name: competitor.label,
                        external_id: competitor.value,
                        // @ts-expect-error meta will have pbId if meta exists
                        pbId: competitor.meta?.pbId,
                      }
                    : { name: competitor.value },
                ),
          firstFinancingDate: values.firstFinancingDate,
          firstFinancingDealType: values.firstFinancingDealType
            ? +values.firstFinancingDealType.value
            : null,
          formerName: values.formerName || null,
          foundedYear: values.foundedYear || null,
          industries: values.industries.map(industry => +industry.value),
          name: values.name.trim(),
          pitchbookUrl: values.pitchbookUrl,
          web: values.web,
          progressBar: calculateCompanyFilledPercent(values) / 100,
          rounds: values.rounds.map(round => ({
            name: round.name,
            subrounds: round.subrounds.map((subround, index) => ({
              index,
              acquiredCalc: +subround.acquiredCalc || null,
              acquiredPercent: +subround.acquiredPercent || null,
              announcedDate: subround.announcedDate,
              isAnnounced: subround.announcedDate ? !subround.noAnnouncement : false,
              ceo: {
                biography: subround.ceo.biography || null,
                education: subround.ceo.education ? +subround.ceo.education.value : null,
                name: subround.ceo.name || null,
                educationHistory: subround.ceo.educationHistory || null,
              },
              ceoChanged: subround.ceoChanged,
              ceoIsFounder: subround.ceoIsFounder,
              dealDate: subround.dealDate,
              dealSize: +subround.dealSize || null,
              employees: +subround.employees || null,
              generatingProfit: subround.generatingProfit,
              generatingRevenue: subround.generatingRevenue,
              investors: subround.investors.map(investor => ({
                corporate: investor.corporate,
                external_id: typeof investor.id === 'number' ? investor.id : null,
                lead: investor.lead,
                name: investor.name,
                new: investor.new,
                pb_id: investor.pbId,
              })),
              participatingPreferred: subround.participatingPreferred,
              postMoneyValuation: +subround.postMoneyValuation || null,
              preMoneyValuation: +subround.preMoneyValuation || null,
              revenueStatusChanged: subround.revenueStatusChanged,
              securityType: subround.securityType ? +subround.securityType.value : undefined,
              cap: +subround.cap || undefined,
              discount: +subround.discount || undefined,
              interest: +subround.interest || undefined,
              conversionAmount: +subround.conversionAmount || undefined,
            })),
          })),
        }).unwrap();

        if (!generatedLink || !submitGeneratedLink) {
          showNotification({
            type: 'info',
            title: response.title,
            message: response.message,
          });
        }

        if (generatedLink) {
          if (submitGeneratedLink) {
            navigate({ pathname: ROUTES.GENERATED_LINK, search: '?status=submitted' });
          } else {
            formik.resetForm({ values: { form: values } });
          }
        } else {
          navigate(ROUTES.COMPANIES);
        }
      } catch (e) {
        const errors = transformCompanyValidationErrors(e);

        if (errors) {
          await handleCompanyFormikErrors(formik, errors, showNotification, roundRefs);

          formik.setErrors(errors);
        } else {
          handleApiError(e);
        }
      }
    },
    [
      companyId,
      draftCompanyPbId,
      formik,
      generatedLink,
      handleApiError,
      navigate,
      saveCompanyAsDraftMutation,
      showNotification,
    ],
  );

  const employees = useMemo(() => {
    let currentRoundIndex = formik.values.form.rounds.length - 1;

    while (currentRoundIndex >= 0) {
      let currentSubroundIndex = formik.values.form.rounds[currentRoundIndex].subrounds.length - 1;

      while (currentSubroundIndex >= 0) {
        if (
          formik.values.form.rounds[currentRoundIndex].subrounds[currentSubroundIndex].employees
        ) {
          return formik.values.form.rounds[currentRoundIndex].subrounds[currentSubroundIndex]
            .employees;
        }

        currentSubroundIndex--;
      }

      currentRoundIndex--;
    }

    return '';
  }, [formik.values.form.rounds]);

  return (
    <>
      <EditingBanner
        text={`The Company Profile is being edited by ${externalUserEmail as string}`}
        visible={!!externalUserEmail}
        onClose={hideEditingBanner}
      />
      <div className={styles.container}>
        <FormikProvider value={formik}>
          {existingCompany && (
            <div className={styles.container__error}>
              <div className={styles['container__error-first-column']}>
                <InfoIcon sx={{ mr: '8px', color: 'var(--error-color)' }} />
                <Typography color="var(--error-color)" variant="subtitle1">
                  This company already exists
                </Typography>
              </div>
              <button
                disabled={isLoadingLazyGetDraftCompanyQuery}
                type="button"
                onClick={handleExistingCompanyProfileView}
              >
                <Typography
                  color="var(--main-2-color)"
                  fontWeight="500"
                  sx={{ textDecorationLine: 'underline' }}
                  variant="body1"
                >
                  View profile
                </Typography>
              </button>
            </div>
          )}
          <div className={clsx(styles.overview, { [styles['overview--error']]: existingCompany })}>
            <div className={styles.overview__status}>
              <Typography
                color="var(--text-3-color)"
                maxWidth="40%"
                overflow="hidden"
                textOverflow="ellipsis"
                variant="h4"
                whiteSpace="nowrap"
              >
                {formik.values.form.name}
              </Typography>
              {!generatedLink && companyStatus && <StatusSteps activeStatus={companyStatus} />}
            </div>
            <div className={styles.overview__info}>
              <div className={styles['overview__info__item']}>
                <EmojiFlagsIcon
                  sx={{
                    color: 'var(--gray-6-color)',
                    width: '20px',
                    height: '20px',
                    mr: '8px',
                  }}
                />
                <Typography
                  alignItems="center"
                  color="var(--text-3-color)"
                  display="flex"
                  variant="subtitle2"
                >
                  Founded
                  <Typography
                    component="span"
                    fontWeight="600"
                    maxWidth="50px"
                    ml="5px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {formik.values.form.foundedYear || '-'}
                  </Typography>
                </Typography>
              </div>
              <div className={styles['overview__info__item']}>
                <PersonAddAltIcon
                  sx={{
                    color: 'var(--gray-6-color)',
                    width: '20px',
                    height: '20px',
                    mr: '8px',
                  }}
                />
                <Typography
                  alignItems="center"
                  color="var(--text-3-color)"
                  display="flex"
                  variant="subtitle2"
                >
                  <Typography
                    component="span"
                    fontWeight="600"
                    maxWidth="100px"
                    mr="5px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {employees || '-'}
                  </Typography>
                  Employees
                </Typography>
              </div>
              <div className={clsx(styles['overview__info__item'], styles['overview__info--bio'])}>
                <InfoIcon
                  sx={{
                    color: 'var(--gray-6-color)',
                    width: '20px',
                    height: '20px',
                    mr: '8px',
                  }}
                />
                <Typography
                  alignItems="center"
                  color="var(--text-3-color)"
                  display="flex"
                  overflow="hidden"
                  variant="subtitle2"
                >
                  Description
                  <Typography
                    component="span"
                    fontWeight="600"
                    ml="5px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    title={formik.values.form.bio}
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {formik.values.form.bio || '-'}
                  </Typography>
                </Typography>
              </div>
            </div>
            <div className={clsx(styles.overview__info, styles['overview__info--websites'])}>
              <div className={styles['overview__info__item']}>
                <LanguageIcon
                  sx={{
                    color: 'var(--gray-6-color)',
                    width: '20px',
                    height: '20px',
                    mr: '8px',
                  }}
                />
                {formik.values.form.web ? (
                  <Link
                    color="rgb(var(--main-2-color-rgb))"
                    fontWeight="500"
                    href={formatUrl(formik.values.form.web)}
                    overflow="hidden"
                    target="_blank"
                    textOverflow="ellipsis"
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {formik.values.form.web}
                  </Link>
                ) : (
                  <Typography fontWeight="600" variant="subtitle2">
                    -
                  </Typography>
                )}
              </div>
              <div className={styles['overview__info__item']}>
                <img alt="pitchbook" src={pitchbook} />
                {formik.values.form.pitchbookUrl ? (
                  <Link
                    color="rgb(var(--main-2-color-rgb))"
                    fontWeight="500"
                    href={formatUrl(formik.values.form.pitchbookUrl)}
                    ml="8px"
                    overflow="hidden"
                    target="_blank"
                    textOverflow="ellipsis"
                    variant="subtitle2"
                    whiteSpace="nowrap"
                  >
                    {formik.values.form.pitchbookUrl}
                  </Link>
                ) : (
                  <Typography fontWeight="600" variant="subtitle2">
                    -
                  </Typography>
                )}
              </div>
            </div>
            <ProgressBarContainer />
            {!generatedLink && (
              <Tabs
                indicatorColor="primary"
                value={activeTabIndex === null ? false : activeTabIndex}
                onChange={handleActiveTabChange}
              >
                <Tab label="General Data" />
                {companyHasHistory && <Tab label="History" />}
              </Tabs>
            )}
          </div>
          {activeTabIndex === 0 && (
            <GeneralInfo
              companyId={companyId}
              companyLocked={companyLocked}
              companyPbId={draftCompanyPbId}
              generatedLink={!!generatedLink}
              loadingSaveCompanyAsDraft={isLoadingSaveCompanyAsDraftMutation}
              roundRefs={roundRefs}
              setExistingCompany={setExistingCompany}
              onSaveAsDraft={handleSaveAsDraft}
            />
          )}
          {activeTabIndex === 1 && companyId && <CompanyHistoryChanges companyId={companyId} />}
        </FormikProvider>
      </div>
      <FullScreenLoader loading={isLoadingLazyGetDraftCompanyQuery || concurrentModeLoading} />
      {!generatedLink && companyStatus === 'draft' && (
        <ConcurrentMode
          companyId={companyId}
          companyLocked={companyLocked}
          loadingSaveAsDraft={isLoadingSaveCompanyAsDraftMutation}
          onCompanyLocked={setCompanyLocked}
          onLoading={setConcurrentModeLoading}
          onSaveAsDraft={handleSaveAsDraft}
        />
      )}
      {blocker.state === 'blocked' && (
        <ConfirmModal
          open
          description="Changes you made may not be saved"
          title="Do you want to proceed?"
          onCancel={blocker.reset}
          onConfirm={blocker.proceed}
        />
      )}
    </>
  );
};
